import keycloak from '../../Keycloak';

export const BASE_URL = import.meta.env.VITE_BASE_URL;

// Deduplicate concurrent refresh attempts. If multiple fetch calls hit a 401
// at the same time, they all wait for the same refresh promise instead of
// each triggering their own (which would race against Keycloak's refresh
// token rotation and cause spurious failures).
let refreshInFlight: Promise<boolean> | null = null;

const refreshTokenOnce = (): Promise<boolean> => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = keycloak
    .updateToken(-1)
    .catch((err) => {
      console.error('keycloak.updateToken failed:', err);
      return false;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
};

const buildAuthOptions = (
  accessToken: string,
  options: RequestInit,
  payload: unknown
): RequestInit => {
  const method = options.method ? options.method.toUpperCase() : 'GET';
  const hasBody = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  return {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: hasBody && payload ? JSON.stringify(payload) : undefined,
  };
};

/**
 * Low-level authenticated fetch.
 *
 * Strategy:
 *  - Use whatever token the Keycloak SDK currently has (single source of truth).
 *  - Do NOT refresh proactively — `autoRefreshToken` from the provider already
 *    schedules refreshes in the background. Calling updateToken before every
 *    request causes races with that scheduler, which the server resolves by
 *    rejecting the rotated refresh_token, which the SDK reads as session loss.
 *  - On 401, attempt recovery in two steps:
 *      1. Re-read `keycloak.token` (autoRefreshToken may have refreshed it
 *         while our request was in-flight — no manual refresh needed).
 *      2. Otherwise force a single deduplicated refresh and retry once.
 *  - Do not throw "Session expired" eagerly; throw a typed HTTP error so the
 *    caller can show a toast instead of being kicked to the login page.
 */
export const fetchWithAuth = async (
  url: string,
  options: RequestInit,
  queryParams: Record<string, string | number> = {},
  payload?: unknown
): Promise<unknown> => {
  const initialToken = keycloak.token;
  if (!initialToken) {
    throw new Error('No access token available');
  }

  const urlWithParams = new URL(url);
  for (const key of Object.keys(queryParams)) {
    urlWithParams.searchParams.append(key, String(queryParams[key]));
  }
  const finalUrl = urlWithParams.toString();

  const doFetch = (token: string) =>
    fetch(finalUrl, buildAuthOptions(token, options, payload));

  let response: Response;
  try {
    response = await doFetch(initialToken);
  } catch (error) {
    console.error('Network error in fetchWithAuth:', error);
    throw error;
  }

  // 401 recovery — try once with a fresh token, twice at most total.
  if (response.status === 401) {
    let nextToken: string | undefined = keycloak.token;
    if (!nextToken || nextToken === initialToken) {
      // No newer token cached yet; trigger a single shared refresh.
      const refreshed = await refreshTokenOnce();
      nextToken = refreshed ? keycloak.token : undefined;
    }
    if (nextToken && nextToken !== initialToken) {
      try {
        response = await doFetch(nextToken);
      } catch (error) {
        console.error('Network error on 401 retry:', error);
        throw error;
      }
    }
    // If still 401, fall through to the !ok branch below.
  }

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      /* response had no JSON body */
    }
    const err = new Error(`HTTP ${response.status} on ${url}`) as Error & {
      status?: number;
      body?: unknown;
    };
    err.status = response.status;
    err.body = body;
    throw err;
  }

  return response.json();
};

export const buildQueryParams = (
  params: Record<string, string | number | boolean | null | undefined>
) =>
  Object.entries(params)
    .map(([key, value]) =>
      value !== undefined && value !== null && value !== '' && value !== false
        ? `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
        : ''
    )
    .join('&');

// URLSearchParams only accepts Record<string, string>; stringify numeric values.
export const toSearchParams = (params: Record<string, string | number>) =>
  new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).map(([k, v]) => [k, String(v)])
    )
  );

// Coerces fetchWithAuth's unknown response to a number; returns 0 for anything
// that is not a finite number (error body, null, object, etc.).
export const toNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;
